import { t } from "@travily/i18n";
import { useClampedText } from "@ui/hooks/use-clamped-text";
import { tv, type VariantProps } from "@ui/lib/tv";
import type { ReactNode } from "react";

const expandableText = tv({
  defaultVariants: {
    color: "white",
  },
  slots: {
    root: "",
    collapsed: "relative overflow-hidden",
    overlay: "absolute bottom-0 right-0 pl-10",
    toggle: "cursor-pointer font-medium",
  },
  variants: {
    color: {
      white: {
        overlay: "bg-gradient-to-r from-transparent via-white via-[25%] to-white",
        toggle: "text-brand-tertiary",
      },
      yellow: {
        overlay: "bg-gradient-to-r from-transparent via-yellow-50 via-[25%] to-yellow-50",
        toggle: "text-yellow-700",
      },
    },
  },
});

type ExpandableTextVariants = VariantProps<typeof expandableText>;

export interface ExpandableTextProps {
  children: ReactNode;
  className?: string;
  color?: ExpandableTextVariants["color"];
  lessLabel?: string;
  lines?: number;
  moreLabel?: string;
  overlayClassName?: string;
}

export function ExpandableText({
  children,
  className,
  color,
  lessLabel,
  lines = 3,
  moreLabel,
  overlayClassName,
}: ExpandableTextProps) {
  const resolvedLessLabel = lessLabel ?? t("common.actions.view_less");
  const resolvedMoreLabel = moreLabel ?? t("common.actions.view_more");
  const styles = expandableText({ color });
  const { collapse, expand, expanded, isClamped, ref, style } = useClampedText({ lines });

  if (expanded) {
    return (
      <div className={styles.root({ className })}>
        <p className="whitespace-pre-line">
          {children}{" "}
          <button className={styles.toggle()} onClick={collapse} type="button">
            {resolvedLessLabel}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.root({ className })}>
      <div ref={ref} className={styles.collapsed()} style={style}>
        <p className="whitespace-pre-line">{children}</p>
        {isClamped && (
          <span className={styles.overlay({ className: overlayClassName })}>
            {/* eslint-disable-next-line i18next/no-literal-string -- typographic ellipsis, not translatable */}
            {"... "}
            <button className={styles.toggle()} onClick={expand} type="button">
              {resolvedMoreLabel}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
